import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from '@/types';
import '@hsablonniere/activity-graph';

interface ActivityGraphProps {
    user: User | null;
}

export const ActivityGraph: React.FC<ActivityGraphProps> = ({ user }) => {
    const { t } = useTranslation(['profile']);
    const activityGraphRef = useRef<HTMLElement | null>(null);

    // Convert activity_map to format required by activity-graph component
    const activityGraphData = useMemo(() => {
        if (!user?.stats?.activity_map) {
            return {};
        }

        const activityMap = user.stats.activity_map;
        const activityMapDetailed = user.stats.activity_map_detailed;
        const activityValues = Object.values(activityMap);
        const maxActivity = activityValues.length > 0 ? Math.max(...activityValues, 1) : 1;

        // Convert to activity-graph format
        const data: Record<string, { title?: string; parts?: string[] }> = {};

        Object.entries(activityMap).forEach(([dateKey, activity]) => {
            const normalized = maxActivity > 0 ? Math.min(4, Math.floor((activity / maxActivity) * 4)) : 0;
            const level = normalized;
            const styleParts = level > 0 ? [`level-${level}`] : [];

            let contributionText = '';
            // We use i18n keys manually constructed or passed, but simpler to use hard strings for now and refactor later or use translation function inside if possible.
            // Actually, we can use t function here.

            if (activityMapDetailed && activityMapDetailed[dateKey]) {
                const detailed = activityMapDetailed[dateKey];
                const textParts: string[] = [];
                if (detailed.posts > 0) {
                    // "X bài đăng"
                    textParts.push(`${detailed.posts} ${t('activity.posts', { count: detailed.posts })}`);
                }
                if (detailed.comments > 0) {
                    textParts.push(`${detailed.comments} ${t('activity.comments', { count: detailed.comments })}`);
                }
                contributionText = textParts.length > 0 ? textParts.join(', ') : t('activity.noActivity');
            } else {
                contributionText = activity === 1 ? t('activity.oneContribution') : t('activity.contributions', { count: activity });
            }

            data[dateKey] = {
                title: contributionText,
                parts: styleParts.length > 0 ? styleParts : undefined
            };
        });

        return data;
    }, [user, t]);

    useEffect(() => {
        if (!activityGraphRef.current) return;
        const element = activityGraphRef.current as any;
        const setData = () => {
            if (element && typeof element.data !== 'undefined') {
                element.data = activityGraphData || {};
            }
        };
        setData();
        const timer = setTimeout(setData, 200);
        return () => clearTimeout(timer);
    }, [activityGraphData]);

    return (
        <div className="activity-section mb-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{t('activity.title')}</h3>
                <div className="flex items-center text-sm text-gray-500 gap-2">
                    <span>{t('activity.legendLess')}</span>
                    <div className="flex gap-1" style={{ '--activity-graph-level-0-bg': '#f3f4f6', '--activity-graph-level-1-bg': '#e0e7ff', '--activity-graph-level-2-bg': '#c7d2fe', '--activity-graph-level-3-bg': '#a5b4fc', '--activity-graph-level-4-bg': '#818cf8' } as React.CSSProperties}>
                        <div className="w-3 h-3 rounded-sm bg-[var(--activity-graph-level-0-bg)]"></div>
                        <div className="w-3 h-3 rounded-sm bg-[var(--activity-graph-level-1-bg)]"></div>
                        <div className="w-3 h-3 rounded-sm bg-[var(--activity-graph-level-2-bg)]"></div>
                        <div className="w-3 h-3 rounded-sm bg-[var(--activity-graph-level-3-bg)]"></div>
                        <div className="w-3 h-3 rounded-sm bg-[var(--activity-graph-level-4-bg)]"></div>
                    </div>
                    <span>{t('activity.legendMore')}</span>
                </div>
            </div>
            <div className="border rounded-xl p-4 bg-white shadow-sm overflow-hidden">
                {/* @ts-ignore */}
                <activity-graph ref={activityGraphRef}></activity-graph>
            </div>
        </div>
    );
};
